// Header-only image dimension reader for the uploader's pixel-budget pre-flight
// (media-04). It NEVER decodes the image — no createImageBitmap, no <img>: a
// 134 MP panorama decodes to ~540 MB in the tab, which is exactly what the
// pre-flight exists to avoid. It reads at most the first 1 MiB of the file and
// parses the container header (JPEG SOFn, PNG IHDR, GIF logical screen
// descriptor, WebP VP8/VP8L/VP8X).
//
// Everything here FAILS OPEN: an unknown format (HEIC, TIFF, AVIF…), a truncated
// or malformed header, or a read error yields `null`, and callers must treat
// `null` as "allow the upload". The server backstop (imaging.MaxDecodePixels →
// property_images.processing_error = 'too_large') catches anything that slips
// past.

export interface ImageDimensions {
  width: number;
  height: number;
}

// HEADER_READ_LIMIT caps how many bytes the pre-flight reads. JPEG SOF can sit
// behind large APP1 (EXIF + embedded thumbnail) / APP2 (ICC) segments, which in
// practice stay well under 1 MiB.
export const HEADER_READ_LIMIT = 1 << 20;

export async function readImageDimensions(file: Blob): Promise<ImageDimensions | null> {
  let buf: ArrayBuffer;
  try {
    buf = await file.slice(0, HEADER_READ_LIMIT).arrayBuffer();
  } catch {
    return null;
  }
  try {
    return parseImageDimensions(new Uint8Array(buf));
  } catch {
    return null;
  }
}

// parseImageDimensions is the pure parser over the header bytes (exported for
// tests). Returns null for anything it cannot positively identify.
export function parseImageDimensions(b: Uint8Array): ImageDimensions | null {
  const dims = parseJPEG(b) ?? parsePNG(b) ?? parseGIF(b) ?? parseWebP(b);
  if (!dims || dims.width <= 0 || dims.height <= 0) return null;
  return dims;
}

// exceedsPixelBudget mirrors Go's imaging.ExceedsPixelBudget: true only when
// width*height is strictly above the budget. Unknown/non-positive dims or a
// non-positive budget → false (fail open).
export function exceedsPixelBudget(dims: ImageDimensions | null, budget: number): boolean {
  if (!dims || !(budget > 0)) return false;
  if (!(dims.width > 0) || !(dims.height > 0)) return false;
  return dims.width * dims.height > budget;
}

const u16be = (b: Uint8Array, i: number) => (b[i] << 8) | b[i + 1];
const u16le = (b: Uint8Array, i: number) => b[i] | (b[i + 1] << 8);
const u24le = (b: Uint8Array, i: number) => b[i] | (b[i + 1] << 8) | (b[i + 2] << 16);
const u32be = (b: Uint8Array, i: number) =>
  b[i] * 0x1000000 + ((b[i + 1] << 16) | (b[i + 2] << 8) | b[i + 3]);
const ascii = (b: Uint8Array, i: number, s: string) => {
  if (i + s.length > b.length) return false;
  for (let k = 0; k < s.length; k++) if (b[i + k] !== s.charCodeAt(k)) return false;
  return true;
};

function parseJPEG(b: Uint8Array): ImageDimensions | null {
  if (b.length < 4 || b[0] !== 0xff || b[1] !== 0xd8) return null;
  let i = 2;
  while (i < b.length) {
    // Resync to the next marker (tolerates stray bytes) and skip 0xFF fill bytes.
    if (b[i] !== 0xff) {
      i++;
      continue;
    }
    while (i < b.length && b[i] === 0xff) i++;
    if (i >= b.length) return null;
    const marker = b[i];
    i++; // i now points at the segment length (for segments that have one)
    // Standalone markers without a length: TEM, RST0-7, SOI.
    if (marker === 0x01 || (marker >= 0xd0 && marker <= 0xd8)) continue;
    // SOS (entropy-coded data follows) or EOI before any SOF → give up.
    if (marker === 0xda || marker === 0xd9) return null;
    if (i + 2 > b.length) return null;
    const len = u16be(b, i);
    if (len < 2) return null;
    // SOF0–SOF15 except DHT (C4), JPG (C8), DAC (CC).
    if (marker >= 0xc0 && marker <= 0xcf && marker !== 0xc4 && marker !== 0xc8 && marker !== 0xcc) {
      // len(2) precision(1) height(2) width(2)
      if (i + 7 > b.length) return null;
      return { height: u16be(b, i + 3), width: u16be(b, i + 5) };
    }
    i += len;
  }
  return null;
}

function parsePNG(b: Uint8Array): ImageDimensions | null {
  const sig = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];
  if (b.length < 24) return null;
  for (let k = 0; k < sig.length; k++) if (b[k] !== sig[k]) return null;
  if (!ascii(b, 12, "IHDR")) return null;
  return { width: u32be(b, 16), height: u32be(b, 20) };
}

function parseGIF(b: Uint8Array): ImageDimensions | null {
  if (b.length < 10 || !(ascii(b, 0, "GIF87a") || ascii(b, 0, "GIF89a"))) return null;
  return { width: u16le(b, 6), height: u16le(b, 8) };
}

function parseWebP(b: Uint8Array): ImageDimensions | null {
  if (b.length < 30 || !ascii(b, 0, "RIFF") || !ascii(b, 8, "WEBP")) return null;
  if (ascii(b, 12, "VP8 ")) {
    // Lossy: 3-byte frame tag, then start code 9D 01 2A, then 14-bit dims (LE).
    if (b[23] !== 0x9d || b[24] !== 0x01 || b[25] !== 0x2a) return null;
    return { width: u16le(b, 26) & 0x3fff, height: u16le(b, 28) & 0x3fff };
  }
  if (ascii(b, 12, "VP8L")) {
    // Lossless: signature 0x2F, then 14-bit (width-1) and 14-bit (height-1).
    if (b[20] !== 0x2f) return null;
    const width = 1 + (b[21] | ((b[22] & 0x3f) << 8));
    const height = 1 + ((b[22] >> 6) | (b[23] << 2) | ((b[24] & 0x0f) << 10));
    return { width, height };
  }
  if (ascii(b, 12, "VP8X")) {
    // Extended: flags(1) reserved(3), then 24-bit (canvas width-1), (height-1).
    return { width: 1 + u24le(b, 24), height: 1 + u24le(b, 27) };
  }
  return null;
}
